import type { IVideo } from "@aix/vidmax-shared/src/constants/prismaTypings"
import { EVideoTemplate } from "@aix/vidmax-shared/src/remotion/schema/project"
import { createReelFarmProjectProps } from "@aix/vidmax-shared/src/utils/projectProps"
import { isReelFarmVideo } from "@aix/vidmax-shared/src/utils/validation"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import { VideoEditorHeader } from "~/components/Templates/Common/VideoEditorHeader"
import VideoEditorPage from "~/components/Templates/Common/VideoEditorPage"
import { ReelFarmEditor } from "~/components/Templates/ReelFarm/ReelFarmEditor"
import { ReelFarmEditorProvider } from "~/components/Templates/ReelFarm/ReelFarmEditorContext"
import { useReelFarmEditor } from "~/components/Templates/ReelFarm/useReelFarmEditor"
import { api } from "~/utils/api"

const ReelFarmEditorPage = () => {
  const { videoId } = Route.useParams()

  // Fetch video data
  const {
    data: video,
    isLoading,
    error,
  } = api.video.getVideoById.useQuery(
    { id: videoId },
    {
      enabled: !!videoId,
      refetchOnWindowFocus: false,
    },
  )

  // Create project props for the editor
  const projectProps = useMemo(() => {
    if (!video) return null
    return createReelFarmProjectProps(video)
  }, [video])

  if (!video || !projectProps || !isReelFarmVideo(video)) {
    return (
      <VideoEditorPage
        video={video}
        isLoading={isLoading}
        error={error}
        expectedTemplateType={EVideoTemplate.GENZ_UGC_AVATARS}
      >
        <div />
      </VideoEditorPage>
    )
  }

  return (
    <ReelFarmEditorProvider video={video} projectProps={projectProps}>
      <ReelFarmEditorContent />
    </ReelFarmEditorProvider>
  )
}

// Component that uses the ReelFarmEditor context
const ReelFarmEditorContent = () => {
  const {
    video,
    renderStatus,
    saveChanges,
    isUpdating,
    undo,
    redo,
    canUndo,
    canRedo,
    saveAndRender,
    isDurationValid,
    estimatedDuration,
    checkDurationBeforeAction,
    hasChanges,
  } = useReelFarmEditor()

  // Function to get the video title
  const getTitleText = (video: IVideo) => {
    return video.title || "ReelFarm Video"
  }

  return (
    <VideoEditorPage
      video={video}
      isLoading={false}
      error={null}
      expectedTemplateType={EVideoTemplate.GENZ_UGC_AVATARS}
      renderStatus={renderStatus}
      getTitleText={getTitleText}
    >
      <VideoEditorHeader
        title={video?.title || "ReelFarm Editor"}
        video={video}
        saveChanges={saveChanges}
        isUpdating={isUpdating}
        undo={undo}
        redo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        saveAndRender={saveAndRender}
        renderStatus={renderStatus}
        isDurationValid={isDurationValid}
        estimatedDuration={estimatedDuration}
        checkDurationBeforeAction={checkDurationBeforeAction}
        hasChanges={hasChanges}
      />
      <ReelFarmEditor />
    </VideoEditorPage>
  )
}

export const Route = createFileRoute("/videos/$videoId/editor/genz-avatars")({
  component: ReelFarmEditorPage,
})
